import { useEffect, useState, useRef } from "react";
import { Auth, Hub } from "aws-amplify";
import { useLazyQuery } from "@apollo/client";
import { jwtDecode } from "jwt-decode";
import { makeCookieString, getAuthToken } from "../utils/cookie";
import { USER } from "../graphql/queries";

export const useLogin = () => {
  const [isLoggedin, setIsLoggedin] = useState(false);
  const [userNotExist, setUserNotExist] = useState(false);
  const currentUserRef = useRef({});
  const [user, setUser] = useState({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    userPicture: null,
  });
  const [userRole, setUserRole] = useState("");

  const [getUser] = useLazyQuery(USER, {
    onCompleted: (data) => {
      if (data.indParents.length > 0) {
        const currentEmail = currentUserRef?.current?.email;
        const currentPhone = currentUserRef?.current?.phone;
        const currentUsername = currentUserRef?.current?.username;
        const isMatchedUser = (person = {}) =>
          person.email === currentEmail ||
          person.phone === currentPhone ||
          person.username === currentUsername;

        const matchedPupil = data.indParents
          .flatMap((entry) => entry.pupil || [])
          .find((child) => isMatchedUser(child));
        const matchedParent = data.indParents
          .map((entry) => entry.parent)
          .find((parent) => isMatchedUser(parent));
        const fallbackParent = data.indParents[0]?.parent || {};

        const userRole = matchedPupil?.id ? "Student" : "Parent";
        setUserRole(userRole);
        setUser(
          userRole === "Student"
            ? {
                ...matchedPupil,
                username: currentUsername,
                userPicture: null,
              }
            : {
                ...(matchedParent || fallbackParent),
                username: currentUsername,
                userPicture: null,
              },
        );
      } else {
        setUserNotExist(true);
      }
    },
  });

  const getTokenClaims = (token) => {
    try {
      return jwtDecode(token);
    } catch (_error) {
      return null;
    }
  };

  const setUserFromTokenClaims = (claims) => {
    const nextUser = {
      firstName: claims?.given_name || "",
      lastName: claims?.family_name || "",
      picture: null,
      username: claims?.["cognito:username"] || claims?.username || "",
      email: claims?.email || "",
      phone: claims?.phone_number || "",
    };

    setUser(nextUser);
    currentUserRef.current = nextUser;
  };

  const fetchUserProfileFromClaims = (claims) => {
    const email = claims?.email || "";
    const phone = claims?.phone_number || "";
    const username = claims?.["cognito:username"] || claims?.username || "";

    if (!email && !phone && !username) return;

    getUser({
      variables: {
        where: {
          OR: [
            {
              pupil: {
                some: {
                  ...(email ? { email: { equals: email } } : {}),
                  ...(phone ? { phone: { equals: phone } } : {}),
                  ...(!email && !phone ? { username: { equals: username } } : {}),
                },
              },
            },
            {
              parent: {
                ...(email ? { email: { equals: email } } : {}),
                ...(phone ? { phone: { equals: phone } } : {}),
                ...(!email && !phone ? { username: { equals: username } } : {}),
              },
            },
          ],
        },
      },
    });
  };

  Hub.listen("auth", ({ payload: { event, data } }) => {
    switch (event) {
      case "signIn":
        setIsLoggedin(true);
        document.cookie = makeCookieString(
          "ninja_session",
          data.signInUserSession.idToken.jwtToken,
          6,
        );
        getAWSUser();
        break;
      case "signOut":
        setIsLoggedin(false);
        break;
      default:
      // do nothing
    }
  });

  const getAWSUser = async () => {
    try {
      const userInfo = await Auth.currentAuthenticatedUser();
      setUser({
        firstName: userInfo.attributes.given_name,
        lastName: userInfo.attributes.family_name,
        picture: null,
        username: userInfo.username,
        email: userInfo.attributes.email,
        phone: userInfo.attributes.phone_number,
      });
      currentUserRef.current = {
        firstName: userInfo.attributes.given_name,
        lastName: userInfo.attributes.family_name,
        username: userInfo.username,
        picture: null,
        email: userInfo.attributes.email,
        phone: userInfo.attributes.phone_number,
      };
      getUser({
        variables: {
          where: {
            OR: [
              {
                pupil: {
                  some: {
                    ...(userInfo.attributes.email
                      ? { email: { equals: userInfo.attributes.email } }
                      : {}),
                    ...(userInfo.attributes.phone_number
                      ? { phone: { equals: userInfo.attributes.phone_number } }
                      : {}),
                    ...(!userInfo.attributes.email &&
                    !userInfo.attributes.phone_number
                      ? { username: { equals: userInfo.username } }
                      : {}),
                  },
                },
              },
              {
                parent: {
                  ...(userInfo.attributes.email
                    ? { email: { equals: userInfo.attributes.email } }
                    : {}),
                  ...(userInfo.attributes.phone_number
                    ? { phone: { equals: userInfo.attributes.phone_number } }
                    : {}),
                  ...(!userInfo.attributes.email &&
                  !userInfo.attributes.phone_number
                    ? { username: { equals: userInfo.username } }
                    : {}),
                },
              },
            ],
          },
        },
      });
      const currentSession = await Auth.currentSession();
      userInfo.refreshSession(
        currentSession.refreshToken,
        async (err, session) => {
          const { idToken } = session;
          document.cookie = makeCookieString(
            "ninja_session",
            idToken.jwtToken,
            idToken.payload.exp,
            true,
          );
        },
      );
    } catch {
      setIsLoggedin(false);
    }
  };

  const getAccessJwtToken = async () => {
    // Auth.currentSession() checks if token is expired and refreshes with Cognito if needed automatically
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const exp = session.getIdToken().payload.exp;
      if (token) {
        setIsLoggedin(true);
        document.cookie = makeCookieString("ninja_session", token, exp, true);
        getAWSUser();
      }
    } catch {
      const token = getAuthToken();
      if (!token) return;

      const claims = getTokenClaims(token);
      if (!claims) return;

      setIsLoggedin(true);
      setUserFromTokenClaims(claims);
      fetchUserProfileFromClaims(claims);
    }
  };

  useEffect(() => {
    if (!isLoggedin) {
      getAccessJwtToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedin]);

  return [isLoggedin, user, userRole, userNotExist];
};
